import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {useNavigate} from 'react-router-dom'
import {GET, POST, PUT} from '../../../services/api'
import {Input} from '../../../components/Input/Input'
import {EditorQuill} from '../../../components/EditorQuill/EditorQuill'
import {Loading} from '../../../components/Loading/Loading'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Button} from '../../../components/Button/Button'
import {i18n} from '../../../translate/i18n'
import Swal from 'sweetalert2'
import uuid from 'react-uuid'

export interface Children {
  child: number
  created: string
  pluginspaceId: number
  childPluginspace: ChildPluginspace
  updated: string
}
export interface ChildPluginspace {
  id: number
  name: string
  corporateName: string
  cnpj: string
  hash: string
  subdomain: string
  type: 'food' | 'hospitality' | string // pode expandir conforme necessário
  imageLogin: string | null
  permission: string | null
  content: {
    theme: {
      admin: string
      logo: string
    }
  }
  skinId: number
  image: string | null
  terms: boolean
  appLogin: boolean
  gmt: string // ex: "-3:00"
  translate: boolean
  createdUserId: number
  confirmationDateTime: string | null
  confirmationHash: string | null
  status: boolean | string
  created: string // ISO datetime
  updated: string // ISO datetime
  deleted: string | null
  removed: number
  parent: number | null
  storeCode: string | null
  defaultLanguage: string // "pt", "en", etc.
  lead: number
  category: string | null
  defaultCurrency: string // ex: "R$"
  resaleId: string | null
  resellerId: string | null
  emailContact: string | null
  phoneContact: string | null
  wholeSaler: boolean
}

export enum VinculationType {
  DEFAULT = 'default',
  DYNAMIC = 'dynamic',
}

const HotelCreateStore: FC = () => {
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const [children, setChildren] = useState<Children[]>([])
  const [selectedChildId, setSelectedChildId] = useState<number | string | null>(null)
  const [link, setLink] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [status, setStatus] = useState<string>('active')
  const [cnpj, setCNPJ] = useState<string>('')
  const [storeCode, setStoreCode] = useState<string | null>(null)
  const [storeType, setStoreType] = useState<string | null>(null)
  const [order, setOrder] = useState<string>('0')
  const [paymentIntegrated, setPaymentIntegrated] = useState<string>('inactive')
  const [image, setImage] = useState<File | null>(null)
  const [vinculationType, setVinculationType] = useState<VinculationType>(VinculationType.DEFAULT)
  const ensureDescription = (html: string) => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(html, 'text/html')
    const text = doc.body.textContent?.trim() || ''

    return text === '' ? '' : html.trim()
  }

  const handleSave = async () => {
    try {
      setLoading(true)

      if (!storeCode) {
        Swal.fire({
          icon: 'warning',
          title: String(i18n.t('global.warning')),
          text: String(i18n.t('stores_pages_create.messages.select_store_warning')),
        })
        return
      }

      const res = await POST('/stores-hotel', {
        link: link || undefined,
        description: ensureDescription(description) || undefined,
        status: status === 'active' ? true : false,
        code: storeCode,
        order: order !== undefined && order !== null && order !== '' ? Number(order) : undefined,
        paymentIntegrated: paymentIntegrated === 'active' ? true : false,
        vinculationType,
      })

      if (!res.success) {
        Swal.fire({
          icon: 'error',
          title: String(i18n.t('global.error')),
          text: res.error.message || String(i18n.t('stores_pages_create.messages.error_saving')),
        })
        return
      }

      if (res.success && image) {
        try {
          const form = new FormData()
          form.append('file', image)
          form.append('nameImage', `store-${uuid()}.jpg`)
          form.append('originalSize', 'true')
          const imageRes = await POST('/upload-images/upload-one', form)
          if (imageRes && imageRes.success) {
            await PUT(`/stores-hotel/${res.data.id}`, {img: imageRes.image})
          }
        } catch (err) {
          console.log(
            String(i18n.t('stores_pages_create.console_messages.image_upload_error')),
            err
          )
        }
      }

      Swal.fire({
        icon: 'success',
        title: String(i18n.t('global.success')),
        text: String(i18n.t('stores_pages_create.messages.success_saved')),
      }).then(() => {
        navigate('/hospitality/configuracoes/stores')
      })

      console.log(res)
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: String(i18n.t('global.error')),
        text: String(i18n.t('stores_pages_create.messages.error_saving')),
      })
    } finally {
      setLoading(false)
    }
  }

  const getStores = async () => {
    try {
      const response = await GET(`store-relations/children`)
      if (response && response.data) {
        const stores = response.data.filter(
          (store: Children) => store.childPluginspace.type !== 'hospitality'
        )
        setChildren(stores)
      }
    } catch (error) {
      console.log(error)
      setChildren([])
    }
  }

  const init = async () => {
    setLoading(true)
    await getStores()
    setLoading(false)
  }

  useEffect(() => {
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>
                  {i18n.t('stores_pages_create.title')}
                </span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  {i18n.t('stores_pages_create.subtitle')}
                </span>
              </h3>
            </div>
            <div className='card-body'>
              <div className='row'>
                <div className='col-md-6'>
                  <Input
                    inputAttr={{type: 'select'}}
                    label={i18n.t('stores_pages_create.form.establishment')}
                    placeholder={i18n.t('stores_pages_create.form.select_placeholder')}
                    search={true}
                    options={children.map((c) => ({
                      value: c.childPluginspace.id,
                      select: c.childPluginspace.name,
                    }))}
                    value={
                      children.find((c) => c.childPluginspace.id === selectedChildId)
                        ? {
                            value: selectedChildId,
                            label: children.find((c) => c.childPluginspace.id === selectedChildId)
                              ?.childPluginspace.name,
                          }
                        : ''
                    }
                    change={(opt: any) => {
                      // react-select retorna objeto ou array
                      const value = opt && opt.value ? opt.value : null
                      setSelectedChildId(value)
                      const child = children.find((c) => c.childPluginspace.id === value)
                      if (child) {
                        setDescription('')
                        setStatus(child.childPluginspace.status ? 'active' : 'inactive')
                        setCNPJ(child.childPluginspace.cnpj || '')
                        setStoreCode(
                          child.childPluginspace.id ? String(child.childPluginspace.id) : null
                        )
                        setStoreType(child.childPluginspace.type || null)
                      }
                    }}
                    required={true}
                  />
                </div>
                <div className='col-md-6'>
                  <Input
                    inputAttr={{type: 'default'}}
                    label={i18n.t('stores_pages_create.form.link')}
                    placeholder={i18n.t('stores_pages_create.form.link_placeholder')}
                    value={link}
                    change={(value: string) => setLink(value)}
                    maxCaracteres={200}
                  />
                </div>

                <div className='row'>
                  <div className='col-md-4'>
                    <Input
                      inputAttr={{type: 'select'}}
                      label={i18n.t('stores_pages_create.form.status')}
                      options={[
                        {
                          value: 'active',
                          select: i18n.t('stores_pages_create.form.status_options.active'),
                        },
                        {
                          value: 'inactive',
                          select: i18n.t('stores_pages_create.form.status_options.inactive'),
                        },
                      ]}
                      value={status}
                      required={true}
                      change={(v: any) => setStatus(v)}
                    />
                  </div>
                  <div className='col-md-4'>
                    <Input
                      inputAttr={{type: 'select'}}
                      label={i18n.t('stores_pages_create.form.payment_integrated')}
                      options={[
                        {
                          value: 'active',
                          select: i18n.t('stores_pages_create.form.payment_options.active'),
                        },
                        {
                          value: 'inactive',
                          select: i18n.t('stores_pages_create.form.payment_options.inactive'),
                        },
                      ]}
                      value={paymentIntegrated}
                      change={(v: any) => setPaymentIntegrated(v)}
                      required={true}
                    />
                  </div>
                  <div className='col-md-4'>
                    <Input
                      inputAttr={{type: 'default'}}
                      label={i18n.t('stores_pages_create.form.order')}
                      value={order}
                      placeholder={i18n.t('stores_pages_create.placeholders.order')}
                      change={(v: any) => setOrder(v)}
                    />
                  </div>
                </div>

                <div className='row'>
                  <div className='col-md-4'>
                    <Input
                      inputAttr={{type: 'select'}}
                      label={i18n.t('stores_pages_edit.form.vinculation_label')}
                      options={[
                        {
                          value: 'default',
                          select: i18n.t('stores_pages_edit.form.vinculation_options.default'),
                        },
                        {
                          value: 'dynamic',
                          select: i18n.t('stores_pages_edit.form.vinculation_options.dynamic'),
                        },
                      ]}
                      value={vinculationType}
                      required={true}
                      change={(v: VinculationType) => setVinculationType(v)}
                    />
                  </div>
                </div>

                <div className='col-md-4'>
                  <Input
                    inputAttr={{type: 'image'}}
                    label={i18n.t('stores_pages_create.form.image_label')}
                    value={image}
                    change={(value: any) => {
                      if (value === null) {
                        setImage(null)
                        return
                      }
                      if (value instanceof File) {
                        setImage(value)
                        return
                      }
                      const file = value?.target?.files && value.target.files[0]
                      setImage(file ?? null)
                    }}
                    smalltext={i18n.t('stores_pages_create.form.image_smalltext')}
                    paddingLeft='0'
                    marginTop='mt-6'
                  />
                </div>
              </div>

              <div className='row mt-6'>
                <div className='col-md-12'>
                  <label className='form-label fw-normal'>
                    {i18n.t('stores_pages_create.form.description')}
                  </label>
                  <EditorQuill
                    value={description}
                    change={(v: any) => setDescription(v)}
                    maxLength={100}
                  />
                </div>
              </div>

              <div className='row mt-6'>
                <div className='col-12'>
                  <h5 className='mb-4'>{i18n.t('stores_pages_create.form.info_title')}</h5>
                </div>

                <div className='col-md-4'>
                  <Input
                    inputAttr={{type: 'cnpj'}}
                    label={i18n.t('stores_pages_create.form.cnpj')}
                    value={cnpj}
                    placeholder={i18n.t('stores_pages_create.placeholders.cnpj')}
                    change={(v: any) => setCNPJ(v)}
                    disabled={true}
                  />
                </div>

                <div className='col-md-4'>
                  <Input
                    inputAttr={{type: 'default'}}
                    label={i18n.t('stores_pages_create.form.code')}
                    value={storeCode || ''}
                    placeholder={i18n.t('stores_pages_create.placeholders.store_id')}
                    change={(v: any) => setStoreCode(v)}
                    disabled={true}
                  />
                </div>

                <div className='col-md-4'>
                  <Input
                    inputAttr={{type: 'default'}}
                    label={i18n.t('stores_pages_create.form.type')}
                    value={storeType || ''}
                    placeholder={i18n.t('stores_pages_create.form.type')}
                    change={(v: any) => setStoreType(v)}
                    disabled={true}
                  />
                </div>
              </div>

              {children.length === 0 && (
                <div className='text-center text-muted mt-4'>
                  {i18n.t('stores_pages_create.form.empty_message')}
                </div>
              )}
            </div>
            {/* Botões fixos no bottom da página */}
            <div className='card-footer border-0 pt-0 d-flex justify-content-between'>
              <Button
                text={i18n.t(`buttons.back`)}
                color='info'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={() => navigate('/hospitality/configuracoes/stores')}
                btnClass='fw-bolder'
              />
              <Button
                text={i18n.t(`buttons.save`)}
                color='primary'
                size='small'
                horizontalPadding={6}
                btnAttr={{type: 'button'}}
                click={handleSave}
                btnClass='fw-bolder'
              />
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const HotelCreateStores: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: i18n.t('permission_routes.dashboard'), path: 'dashboard', isActive: false},
          {
            title: i18n.t('stores_pages_create.title'),
            path: 'hospitality/configuracoes/stores/create',
            isActive: true,
          },
        ]}
      >
        {i18n.t('stores_pages_create.title')}
      </PageTitle>
      <HotelCreateStore />
    </>
  )
}

export {HotelCreateStores}
